/**
 * Parse and format AI-generated EHCNA draft sections for on-screen report and PDF export.
 */

export function tryParseJsonContent(raw) {
  if (!raw || typeof raw !== 'string') return null;
  try {
    let cleaned = raw.trim();
    if (cleaned.startsWith('```json')) {
      cleaned = cleaned.replace(/```json\n?/g, '').replace(/```\n?/g, '');
    } else if (cleaned.startsWith('```')) {
      cleaned = cleaned.replace(/```\n?/g, '');
    }
    return JSON.parse(cleaned);
  } catch (_) {
    return null;
  }
}

const AREA_LABELS = {
  cognitionLearning: 'Cognition & Learning',
  communicationInteraction: 'Communication & Interaction',
  semh: 'Social, Emotional & Mental Health',
  sensoryPhysical: 'Sensory & Physical',
};

export function formatAssessSummaryForDisplay(content) {
  const parsed = tryParseJsonContent(content);
  if (!parsed || typeof parsed !== 'object') {
    return { type: 'text', text: content || '' };
  }

  const blocks = Object.entries(parsed).map(([key, value]) => {
    const label = AREA_LABELS[key] || key.replace(/([A-Z])/g, ' $1').trim();
    if (value && typeof value === 'object') {
      return {
        label,
        strengths: value.strengths || value.strength || '',
        needs: value.needs || value.need || '',
      };
    }
    return { label, strengths: String(value || ''), needs: '' };
  });

  return { type: 'assess', blocks };
}

export function formatSmartOutcomesForDisplay(content) {
  const parsed = tryParseJsonContent(content);
  if (!Array.isArray(parsed)) {
    return { type: 'text', text: content || '' };
  }

  const outcomes = parsed.map((item, index) => {
    if (typeof item === 'string') {
      return { index: index + 1, text: item };
    }
    const area = item.area || item.areaOfNeed || '';
    const byWhen = item.byWhen || item.by || '';
    const iWill = item.iWill || item.target || '';
    const soThat = item.soThat || item.purpose || '';
    const sentence = byWhen && iWill && soThat
      ? `By ${byWhen}, the student will ${iWill} so that ${soThat}.`
      : [iWill, soThat].filter(Boolean).join(' ');
    return { index: index + 1, area, text: sentence || JSON.stringify(item) };
  });

  return { type: 'outcomes', outcomes };
}

export function formatPlanDoReviewForDisplay(planDoReview) {
  if (!planDoReview || typeof planDoReview !== 'object') return [];

  return Object.entries(planDoReview)
    .filter(([, cycles]) => Array.isArray(cycles) && cycles.length > 0)
    .map(([area, cycles]) => ({
      area: AREA_LABELS[area] || area,
      cycles: cycles.map((cycle) => ({
        term: cycle.term || `Cycle ${cycle.cycleNumber || ''}`,
        focus: cycle.focus || '',
        targets: (cycle.smartTargets || []).map((t) => ({
          target: t.target || '',
          achieved: t.achieved || '',
          feedback: t.feedback || '',
        })),
        interventions: (cycle.interventions || []).map((i) =>
          [i.name, i.frequency, i.duration, i.ratio].filter(Boolean).join(' • ')
        ),
      })),
    }));
}

export function draftTextValue(field) {
  if (!field) return '';
  if (typeof field === 'string') return field;
  if (typeof field === 'object' && typeof field.content === 'string') return field.content;
  return '';
}

/** Normalize Firebase callable result to generated text */
export function extractCallableContent(result) {
  const data = result?.data;
  if (!data) return null;
  if (typeof data === 'string') return data;
  if (typeof data.content === 'string' && data.content.trim()) return data.content;
  return null;
}
